type GraphemePinyinRow = { glyph: string; pinyin: string };

export type CompactGraphemePinyinLookup = {
  codePoints: Uint32Array;
  otherGlyphs: ReadonlyMap<string, number>;
  pinyinIndexes: Uint32Array;
  readings: readonly string[];
};

function singleCodePoint(glyph: string): number | null {
  const codePoint = glyph.codePointAt(0);
  if (codePoint === undefined) {
    return null;
  }
  return glyph.length === (codePoint > 0xff_ff ? 2 : 1) ? codePoint : null;
}

/** Retains numeric columns and one string per reading, rather than a Map entry per glyph. */
export function createCompactGraphemePinyinLookup(
  rows: readonly GraphemePinyinRow[],
): CompactGraphemePinyinLookup {
  const otherRows = new Map<string, string>();
  const singleRows: { codePoint: number; pinyin: string }[] = [];
  for (const { glyph, pinyin } of rows) {
    const codePoint = singleCodePoint(glyph);
    if (codePoint === null) {
      otherRows.set(glyph, pinyin);
    } else {
      singleRows.push({ codePoint, pinyin });
    }
  }
  singleRows.sort((left, right) => left.codePoint - right.codePoint);

  const readings: string[] = [];
  const readingIndexes = new Map<string, number>();
  const readingIndex = (pinyin: string): number => {
    const existing = readingIndexes.get(pinyin);
    if (existing !== undefined) {
      return existing;
    }
    const index = readings.length;
    readings.push(pinyin);
    readingIndexes.set(pinyin, index);
    return index;
  };

  const codePoints = new Uint32Array(singleRows.length);
  const pinyinIndexes = new Uint32Array(singleRows.length);
  let count = 0;
  for (const [index, row] of singleRows.entries()) {
    // Preserve Map's last-value-wins behavior even if a caller supplies duplicates.
    if (singleRows[index + 1]?.codePoint === row.codePoint) {
      continue;
    }
    codePoints[count] = row.codePoint;
    pinyinIndexes[count] = readingIndex(row.pinyin);
    count++;
  }

  const otherGlyphs = new Map<string, number>();
  for (const [glyph, pinyin] of otherRows) {
    otherGlyphs.set(glyph, readingIndex(pinyin));
  }
  return {
    codePoints: count === codePoints.length ? codePoints : codePoints.slice(0, count),
    otherGlyphs,
    pinyinIndexes: count === pinyinIndexes.length ? pinyinIndexes : pinyinIndexes.slice(0, count),
    readings,
  };
}

/** Every stored reading index points into `readings` (both are built together above). */
function readingAt(lookup: CompactGraphemePinyinLookup, index: number): string {
  const reading = lookup.readings[index];
  if (reading === undefined) {
    throw new Error(
      `Compact grapheme pinyin lookup is corrupt: reading index ${index} is outside its ${lookup.readings.length} readings`,
    );
  }
  return reading;
}

export function readCompactGraphemePinyinLookup(
  lookup: CompactGraphemePinyinLookup,
  glyph: string,
): string | null {
  const codePoint = singleCodePoint(glyph);
  if (codePoint === null) {
    const index = lookup.otherGlyphs.get(glyph);
    return index === undefined ? null : readingAt(lookup, index);
  }

  let start = 0;
  let end = lookup.codePoints.length - 1;
  while (start <= end) {
    const middle = (start + end) >>> 1;
    const candidate = lookup.codePoints[middle];
    if (candidate === undefined) {
      throw new Error(`Compact grapheme pinyin lookup is corrupt: missing code point ${middle}`);
    }
    if (candidate === codePoint) {
      const readingIndex = lookup.pinyinIndexes[middle];
      if (readingIndex === undefined) {
        throw new Error(
          `Compact grapheme pinyin lookup is corrupt: missing reading index ${middle}`,
        );
      }
      return readingAt(lookup, readingIndex);
    }
    if (candidate < codePoint) {
      start = middle + 1;
    } else {
      end = middle - 1;
    }
  }
  return null;
}
